import type {IntlShape} from 'react-intl';
import {getInitialBaselineItems, createNodeIdLookup} from '../Util';
import type {BaselineItem, QuoteDesignerCompany, QuoteDesignerProject} from '../BaselineTypes';
import Util from '../../../forecast-app/shared/util/util';
import {idFromGlobalId} from '../../../forecast-app/shared/util/GlobalIdUtil';
import {getProjectIndicatorString} from '../../../forecast-app/shared/components/project-indicator/support/ProjectIndicatorLogic';
import type {QuoteDocumentData, QuoteLineItem} from './resolver/types';

export const QUOTE_EXPIRY_DAYS = 30;
export const FALLBACK_MINUTES_PER_DAY = 480;

export type BuildQuoteDocumentDataOptions = {
	/** Whether the baseline's deliverables mode is on (feature flag + project setting + budget type). */
	deliverablesOn: boolean;
	revision?: number;
	/** Injectable clock for deterministic tests; defaults to the current date. */
	now?: Date;
};

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Local-calendar ISO date (yyyy-mm-dd) — quotes are dated in the author's calendar, not UTC. */
export const toIsoDate = (date: Date): string => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

/** Parses yyyy-mm-dd into a local Date (avoids the UTC shift of Date-parsing ISO strings). */
export const parseIsoDate = (iso: string): Date => {
	const [year, month, day] = iso.split('-').map(Number);
	return new Date(year, (month || 1) - 1, day || 1);
};

/**
 * The running footer's left cell: the company, and how to reach it. An account with no contact
 * email falls back to its name alone rather than printing a stray separator.
 */
export const footerContactLine = (companyName: string, contactEmail: string | null): string =>
	contactEmail ? `${companyName}  |  ${contactEmail}` : companyName;

/** The document's display date format, shared by the PDF and the live preview. */
export const formatQuoteDate = (iso: string, intl: IntlShape): string =>
	intl.formatDate(parseIsoDate(iso), {day: '2-digit', month: 'short', year: 'numeric'});

export const addDaysToIsoDate = (iso: string, days: number): string => {
	const date = parseIsoDate(iso);
	date.setDate(date.getDate() + days);
	return toIsoDate(date);
};

/** The furthest ahead a quote may be dated. Anything beyond it is a typo, not an intention. */
export const MAX_EXPIRY_YEARS_AHEAD = 10;

/**
 * Whether an expiry the picker emitted is one a quote can actually carry: a four-digit ISO date,
 * on or after the earliest allowed day, and inside a sane horizon.
 *
 * A date picker reports every complete date its segments spell. Half-typed years spell real dates -
 * deleting a digit from 2026 leaves the year 202 - and so do days before the quote was issued, so
 * a value being parseable says nothing about it being meant.
 */
export const isExpiryWithinRange = (iso: string, minIso: string): boolean => {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
	const date = parseIsoDate(iso);
	if (Number.isNaN(date.getTime())) return false;
	if (minIso && iso < minIso) return false;
	const horizon = parseIsoDate(minIso || iso);
	horizon.setFullYear(horizon.getFullYear() + MAX_EXPIRY_YEARS_AHEAD);
	return date <= horizon;
};

const isRoleItem = (item: BaselineItem): boolean => Boolean(item.phaseBaselineRoleId);

type DeliverableRef = {id: string; name: string} | null;

const toLineItem = (item: BaselineItem, phaseName: string, deliverable: DeliverableRef): QuoteLineItem => {
	if (isRoleItem(item)) {
		return {
			kind: 'role',
			name: item.roleName || '',
			phaseId: item.phaseId || '',
			phaseName,
			deliverableId: deliverable?.id ?? null,
			deliverableName: deliverable?.name ?? null,
			hours: item.hours || 0,
			rate: item.ratePerHour || 0,
			total: item.valueOfService || 0,
		};
	}
	return {
		kind: 'expense',
		name: item.name || '',
		phaseId: item.phaseId || '',
		phaseName,
		deliverableId: deliverable?.id ?? null,
		deliverableName: deliverable?.name ?? null,
		hours: 0,
		rate: 0,
		total: item.revenue || 0,
	};
};

/**
 * Derives the quote's structured line items and identity facts from the project's baseline.
 * Called once per designer session in create mode; the result is frozen into the published
 * document, including the issued/expiry dates, revision, and the estimation-unit facts the
 * estimate page needs to re-render the snapshot standalone.
 */
export const buildQuoteDocumentData = (
	project: QuoteDesignerProject,
	company: QuoteDesignerCompany,
	options: BuildQuoteDocumentDataOptions
): QuoteDocumentData => {
	const {deliverablesOn} = options;

	const phaseNameById = new Map<string, string>();
	const phaseOrderById = new Map<string, number>();
	createNodeIdLookup(project.phases?.edges?.map(edge => edge?.node) || []).forEach((node, id) => {
		phaseNameById.set(id, (node as {name?: string | null}).name || '');
		phaseOrderById.set(id, phaseOrderById.size);
	});

	// Deliverables are matched on their raw numeric id — the baseline items carry it globalised,
	// the deliverable nodes carry it inside their relay id.
	const deliverableNameByRawId = new Map<string, string>();
	for (const edge of project.deliverables?.edges || []) {
		const node = edge?.node;
		if (node?.id) {
			deliverableNameByRawId.set(String(idFromGlobalId(node.id)), node.name || '');
		}
	}

	const baselineItems = getInitialBaselineItems(project, deliverablesOn);

	const resolveDeliverable = (item: BaselineItem): DeliverableRef => {
		if (!deliverablesOn || !item.deliverableId) return null;
		const rawId = String(idFromGlobalId(item.deliverableId));
		const name = deliverableNameByRawId.get(rawId);
		return name === undefined ? null : {id: rawId, name};
	};

	const lineItems = baselineItems
		// Deliverables mode only shows items assigned to one of the project's deliverables —
		// the quote must match what the Baseline page (and its Create-quote gate) counts.
		.filter(item => !deliverablesOn || resolveDeliverable(item) != null)
		.map(item => toLineItem(item, item.phaseId ? phaseNameById.get(item.phaseId) || '' : '', resolveDeliverable(item)))
		// Presentation order: baseline phase order, roles before expenses within a phase.
		.map((item, index) => ({item, index}))
		.sort((a, b) => {
			const phaseDelta =
				(phaseOrderById.get(a.item.phaseId) ?? Number.MAX_SAFE_INTEGER) -
				(phaseOrderById.get(b.item.phaseId) ?? Number.MAX_SAFE_INTEGER);
			if (phaseDelta !== 0) return phaseDelta;
			if (a.item.kind !== b.item.kind) return a.item.kind === 'role' ? -1 : 1;
			return a.index - b.index;
		})
		.map(({item}) => item);

	const subtotal = Util.roundFloatToTwoDecimals(
		lineItems.filter(item => item.kind === 'role').reduce((sum, item) => sum + item.total, 0)
	);
	const expensesTotal = Util.roundFloatToTwoDecimals(
		lineItems.filter(item => item.kind === 'expense').reduce((sum, item) => sum + item.total, 0)
	);

	const minutesPerDay = project.baselineEstimationMinutesPerDay;
	const issuedDate = toIsoDate(options.now ?? new Date());

	return {
		projectName: project.name || '',
		projectIndicator: getProjectIndicatorString(project.companyProjectId, project.customProjectId),
		clientName: project.client?.name || null,
		companyName: company.name || '',
		companyContactEmail: company.contactEmail || null,
		salespersonName: project.salesperson?.fullName || null,
		currency: project.rateCard?.currency || company.currency || '',
		companyLogoId: company.logoId || null,
		clientLogoId: project.client?.logoId || null,
		issuedDate,
		expiryDate: addDaysToIsoDate(issuedDate, QUOTE_EXPIRY_DAYS),
		revision: options.revision ?? 1,
		estimationUnit: minutesPerDay != null ? 'days' : 'hours',
		minutesPerDay: minutesPerDay ?? FALLBACK_MINUTES_PER_DAY,
		useDeliverables: deliverablesOn && lineItems.some(item => item.deliverableId != null),
		lineItems,
		subtotal,
		expensesTotal,
		total: Util.roundFloatToTwoDecimals(subtotal + expensesTotal),
	};
};
